import { useState } from 'react';
import { Button, Snackbar, Alert } from '@mui/material';
import { FileDownload } from '@mui/icons-material';

export interface ColumnDef {
  field: string;
  header: string;
  format?: (value: unknown, record: Record<string, unknown>) => string;
}

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function generateCSV<T extends Record<string, unknown>>(
  data: T[],
  columns: ColumnDef[],
): string {
  const header = columns.map(c => escapeCSV(c.header)).join(',');
  const rows = data.map(row =>
    columns.map(col => {
      const raw = col.format ? col.format(row[col.field], row) : String(row[col.field] ?? '');
      return escapeCSV(raw);
    }).join(',')
  );
  return [header, ...rows].join('\n');
}

export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export interface CSVExportProps<T extends Record<string, unknown>> {
  data: T[];
  columns: ColumnDef[];
  filename: string;
  label?: string;
  selectedIds?: string[];
  variant?: 'text' | 'outlined' | 'contained';
}

export function CSVExport<T extends Record<string, unknown>>({
  data,
  columns,
  filename,
  label = 'Export CSV',
  selectedIds,
  variant = 'outlined',
}: CSVExportProps<T>) {
  const [error, setError] = useState('');

  const handleExport = () => {
    try {
      const exportData = selectedIds
        ? data.filter(row => selectedIds.includes(String(row.id)))
        : data;

      if (exportData.length === 0) {
        setError('No data to export');
        return;
      }

      const csv = generateCSV(exportData, columns);
      downloadCSV(csv, filename);
    } catch {
      setError('Failed to export CSV');
    }
  };

  return (
    <>
      <Button
        size="small"
        variant={variant}
        startIcon={<FileDownload />}
        onClick={handleExport}
        sx={{ textTransform: 'none' }}
      >
        {label}
      </Button>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
